import {
  axiosGetRequest,
  axiosPostPaginatedRequest,
  axiosPostRequest,
  getAIRoute,
} from '../api';
import { FetchAIPromptsRequestDTO } from './dtos/FetchAIPromptsRequestDTO';
import { SendAIChatbotMessageRequestDTO } from './dtos/SendAIChatbotMessageRequestDTO';
import { SendAIChatbotMessageResponseDTO } from './dtos/SendAIChatbotMessageResponseDTO';
import { SurveyRequest } from './interactiveSurvey/types';
import { validSurveyRequest } from './interactiveSurvey/request';
import type { ChatbotVariant } from '../types/aiChatbot';

export interface ChatCancellationResponse {
  version: 1;
  responseId: string;
  status: 'cancellation_requested' | 'completed' | 'not_found';
}

export const cancelAIChatbotResponseCall = (responseId: string, variant: ChatbotVariant) => {
  const routes = { maya: 'mcpmaya', sylvia: 'mcpsylvia', padre: 'mcppadre' } as const;
  return axiosPostPaginatedRequest<Record<string, never>, ChatCancellationResponse>(
    `${getAIRoute()}/chat/${routes[variant]}/${encodeURIComponent(responseId)}/cancel`,
    {}, undefined, 'AIChatWidget:StopStreaming'
  );
};

export type AIHubQuickReply = { id: string; question: string; onboardingStepIds?: string[] };

export const fetchAIHubQuickRepliesCall = (roomId: string) =>
  axiosGetRequest<AIHubQuickReply[]>(
    `${getAIRoute()}/prompts/quick-replies/${encodeURIComponent(roomId)}`,
    undefined,
    'AIChatWidget:FetchHubQuickReplies'
  ).then(value => {
    if (!Array.isArray(value) || value.some(item =>
      !item || typeof item.id !== 'string' || !item.id.trim() ||
      typeof item.question !== 'string' ||
      (item.onboardingStepIds !== undefined && (!Array.isArray(item.onboardingStepIds) ||
        item.onboardingStepIds.some(id => typeof id !== 'string' || !id.trim())))
    )) throw new Error('Invalid AI hub quick replies');
    return value;
  });

export interface ChatSessionSummary {
  sessionId: string;
  title: string;
  createdAt: string;
  lastUpdatedAt: string;
  messageCount: number;
}

export interface ChatSessionMessage {
  role: 'user' | 'assistant';
  content: string;
  modelName?: string;
  model?: string;
  aiModelName?: string;
  aiReasoning?: string;
  reasoning?: string;
  modelReasoning?: string;
  reasoningMode?: string;
  reasoningEffort?: string;
}

export interface ChatSessionDetail {
  sessionId: string;
  agentId: string;
  messages: ChatSessionMessage[];
}

export const isChatSessionDetailInScope = (
  detail: ChatSessionDetail,
  expectedSessionId: string,
  expectedAgentId: string
): boolean =>
  detail.sessionId === expectedSessionId &&
  detail.agentId.toLowerCase() === expectedAgentId.toLowerCase();

export const fetchChatSessionsCall = (agentId: string, top = 50): Promise<ChatSessionSummary[]> => {
  return axiosGetRequest<ChatSessionSummary[]>(
    `${getAIRoute()}/chat/sessions?top=${top}&agentId=${encodeURIComponent(agentId)}`,
    undefined,
    'AIChatWidget:FetchSessions'
  );
};

export const fetchChatSessionMessagesCall = (sessionId: string): Promise<ChatSessionDetail> => {
  return axiosGetRequest<ChatSessionDetail>(
    `${getAIRoute()}/chat/history/${encodeURIComponent(sessionId)}`,
    undefined,
    'AIChatWidget:FetchSessionMessages'
  );
};

export const sendAIChatbotMessageCall = (
  request: SendAIChatbotMessageRequestDTO | SurveyRequest,
  clientAction: string | undefined,
  responseId: string
) => {
  if ('interactiveSurvey' in request) {
    if (!validSurveyRequest(request) || responseId !== request.interactiveSurvey.interactionId) {
      throw new Error('invalid_contract');
    }
    return axiosPostPaginatedRequest<SurveyRequest, SendAIChatbotMessageResponseDTO>(
      `${getAIRoute()}/chat/mcpmaya`, request, undefined, 'AIChatWidget:SurveyInteraction',
      { 'x-idempotency-key': responseId }, true
    ).catch(() => { throw new Error('survey_outcome_unknown'); });
  }
  const {
    chatbotRoute,
    prompt,
    documentPath,
    documentId,
    postId,
    storyId,
    roomId,
    quickReplyId,
    actionSelection,
    journeyId,
    sessionId,
    contextMode,
    interactiveSurveySchemaVersion,
  } = request;
  if (!responseId.trim()) {
    throw new Error('AI chat requests require a non-empty response ID.');
  }

  return axiosPostPaginatedRequest<
    SendAIChatbotMessageRequestDTO,
    SendAIChatbotMessageResponseDTO
  >(
    `${getAIRoute()}/chat/${chatbotRoute ?? 'mcpmaya'}`,
    {
      prompt,
      documentPath,
      documentId,
      postId,
      storyId,
      roomId,
      ...(quickReplyId ? { quickReplyId } : {}),
      ...(actionSelection ? { actionSelection: { offerId: actionSelection.offerId, optionId: actionSelection.optionId } } : {}),
      journeyId,
      sessionId,
      contextMode,
      supportsHtmlSnapshots: true,
      ...(!chatbotRoute || chatbotRoute === 'mcpmaya'
        ? { interactiveSurveySchemaVersion: interactiveSurveySchemaVersion ?? 1 as const } : {}),
    },
    undefined,
    clientAction,
    {
      'x-idempotency-key': responseId,
    }
  );
};

export const fetchAIPromptsCall = (
  request: FetchAIPromptsRequestDTO,
  clientAction?: string
) => {
  return axiosPostRequest<FetchAIPromptsRequestDTO, string[]>(
    `${getAIRoute()}/prompts/user`,
    request,
    undefined,
    clientAction
  );
};
